import React from "react";

import Link from "next/link";
import { useRouter } from "next/router";

import {
  Avatar,
  Box,
  Button,
  Link as MuiLink,
  Typography,
} from "@mui/material";

import { APP_ROUTES, useAuth } from "market-webapp-commons";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import LogoIcon from "../../../../../public/logo.png";
import StandardTextField from "../../../../components/standard-form-fields/StandardTextField";
import { NEXT_PUBLIC_DOMAIN_NAME } from "../../../../constants/public-constant";
import { LocalizationProvider } from "../../../../contexts/PageLocaleContext";
import TranslationTexts from "./index.translation.json";

const RenderLink = (href) => (children) =>
  <MuiLink href={href}>{children}</MuiLink>;

const StandardTextFieldSx = (theme) => ({
  ".MuiInputBase-root": {
    color: theme.palette.primary.contrastText,
  },
  ".MuiInputLabel-root": {
    color: theme.palette.primary.contrastText,
    "&.Mui-focused": {
      color: theme.palette.primary.contrastText,
    },
    "&.Mui-error": {
      color: theme.palette.warning.main,
      "&.Mui-focused": {
        color: theme.palette.warning.main,
      },
    },
  },
  ".MuiOutlinedInput-root": {
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.contrastText,
    },
    "&:hover": {
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.contrastText,
      },
    },
    "&.Mui-focused": {
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.contrastText,
      },
    },
    "&.Mui-error": {
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.warning.main,
      },
      "&:hover": {
        ".MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.warning.main,
        },
      },
      "&.Mui-focused": {
        ".MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.warning.main,
        },
      },
    },
  },
});

const LoginForm = (props) => {
  const router = useRouter();
  const intl = useIntl();
  const formMethods = useForm({
    defaultValues: {
      username: "",
      password: "",
    },
  });
  const { handleSubmit, setError } = formMethods;

  const { login } = useAuth();

  const submitLoginForm = async (data) => {
    try {
      await login({
        ...data,
        redirect: router.query.redirect ?? APP_ROUTES.HOME,
      });
    } catch (err) {
      if (err.message?.includes("您輸入的用戶名和密碼可能錯誤")) {
        setError("password", {
          message: (
            <FormattedMessage
              id="form-error-invalid"
              values={{
                link: RenderLink(
                  `https://www.${NEXT_PUBLIC_DOMAIN_NAME}/registration/`
                ),
              }}
            />
          ),
        });
      } else {
        setError("password", {
          message: (
            <FormattedMessage
              id="form-error-default"
              values={{
                link: RenderLink("https://forms.gle/4vWFhNEjWrVraEQy5"),
              }}
            />
          ),
        });
      }
    }
  };

  return (
    <Box {...props}>
      <form onSubmit={handleSubmit(submitLoginForm)}>
        <Box
          sx={(theme) => ({
            width: "320px",
            p: 2.5,
            position: "relative",
            overflow: "hidden",
            borderRadius: theme.spacing(1),
            backgroundColor: "rgba(0,0,0,0.5)",
            color: "white",
            transform: "translateX(-50%) translateY(-50%)",
          })}
          elevation="3"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" pb={2}>
            <Avatar
              sx={{ display: "inline-block", width: 60, height: 60 }}
              src={LogoIcon.src}
              alt=""
            />
            <Box
              sx={{
                display: "inline-block",
                ml: 2,
              }}
            >
              <Typography variant="subtitle2">飛天奶茶</Typography>
              <Typography variant="subtitle2">Flying Milk Tea</Typography>
              <Typography variant="body2">
                <FormattedMessage id="company-short-description" />
              </Typography>
            </Box>
          </Box>
          <FormProvider {...formMethods}>
            <StandardTextField
              name="username"
              registerProps={{
                required: intl.formatMessage({
                  id: "form-error-username-empty",
                }),
              }}
              textFieldProps={{
                label: <FormattedMessage id="form-username" />,
                InputLabelProps: {
                  shrink: true,
                },
                margin: "normal",
                size: "small",
                sx: StandardTextFieldSx,
                variant: "outlined",
              }}
              errorMessageProps={{
                color: "warning.main",
              }}
            />
            <StandardTextField
              name="password"
              type="password"
              registerProps={{
                required: intl.formatMessage({
                  id: "form-error-password-empty",
                }),
              }}
              textFieldProps={{
                label: <FormattedMessage id="form-password" />,
                InputLabelProps: {
                  shrink: true,
                },
                margin: "normal",
                size: "small",
                sx: StandardTextFieldSx,
                variant: "outlined",
              }}
              errorMessageProps={{
                color: "warning.main",
              }}
            />
          </FormProvider>
          <Button
            sx={{
              width: "100px",
              mt: 2,
              "&:before": {
                opacity: 0.1,
              },
            }}
            variant="contained"
            color="primary"
            size="small"
            type="submit"
          >
            <FormattedMessage id="form-login" />
          </Button>
          <Box mt={2}>
            <Link
              href={`https://www.${NEXT_PUBLIC_DOMAIN_NAME}/login/?rcp_action=lostpassword`}
              passHref
            >
              <MuiLink style={{ color: "white" }}>
                <FormattedMessage id="form-forget-password" />
              </MuiLink>
            </Link>
            <Typography style={{ color: "white" }} display="inline">
              {" | "}
            </Typography>
            <Link
              href={`https://www.${NEXT_PUBLIC_DOMAIN_NAME}/registration/`}
              passHref
            >
              <MuiLink style={{ color: "white" }}>
                <FormattedMessage id="form-register" />
              </MuiLink>
            </Link>
          </Box>
          <Box mt={2} mb={3}>
            <Link href="/" passHref>
              <MuiLink style={{ color: "white" }}>
                <Typography variant="body2">
                  <FormattedMessage id="home-page" />
                </Typography>
              </MuiLink>
            </Link>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default (props) => (
  <LocalizationProvider translationTexts={TranslationTexts}>
    <LoginForm {...props} />
  </LocalizationProvider>
);
